import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GridDataResult, KENDO_GRID, PageChangeEvent, PagerSettings } from '@progress/kendo-angular-grid';
import { SourcesService } from './sources.service';
import { Observable } from 'rxjs';
import { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { EditSourceComponent } from './child-components/edit-source.component';
import { ConfirmDeleteComponent } from '../items/child-components/confirm-delete.component';
import { SourceResponseModel } from './models/source-response-model';

@Component({
  selector: 'app-sources',
  standalone: true,
  imports: [CommonModule, FormsModule, KENDO_GRID, NgbModule],
  providers: [SourcesService],
  templateUrl: './sources.component.html'
})
export class SourcesComponent implements OnInit {
  public view: Observable<GridDataResult>;
  public error: boolean = false;
  public errorMessage: string = '';
  public validationMessage: string = '';

  public state: State = {
    skip: 0,
    take: 20,
  };

  public pageableSettings: PagerSettings = {
    pageSizes: [10, 20, 50, 100]
  };

  public filter: CompositeFilterDescriptor = {
    logic: 'and',
    filters: [],
  };

  constructor(public sourcesService: SourcesService, private modalService: NgbModal) {
    this.view = sourcesService;
    this.sourcesService.query(this.state, this.filter);
  }

  ngOnInit(): void {}

  public pageChange(state: PageChangeEvent): void {
    this.state.skip = state.skip;
    this.state.take = state.take;
    this.sourcesService.query(this.state, this.filter);
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.filter = filter;
    this.state.skip = 0;

    const stringFields = ['publishingSource', 'description', 'businessContactEmail', 'technicalContactEmail', 'serviceNowGroup'];
    const invalid = stringFields.some(field => {
      const val = this.getFilterValue(filter, field);
      return val.length > 0 && val.length < 3;
    });

    if (invalid) {
      this.validationMessage = 'Please enter at least 3 characters to filter.';
      return;
    }

    this.validationMessage = '';
    this.sourcesService.query(this.state, this.filter);
  }

  public getFilterValue(filter: CompositeFilterDescriptor, field: string): string {
    const descriptor = this.findFieldFilter(filter, field);
    return descriptor ? String(descriptor.value ?? '') : '';
  }

  public onIsActiveFilterChange(event: Event, filter: CompositeFilterDescriptor): void {
    const value = (event.target as HTMLSelectElement).value;
    const newFilters = filter.filters.filter(
      (f): f is FilterDescriptor => !('filters' in f) || (f as any).field !== 'isActive'
    ).filter((f) => !('field' in f) || (f as FilterDescriptor).field !== 'isActive');

    if (value !== '') {
      newFilters.push({ field: 'isActive', operator: 'eq', value: value === 'true' });
    }

    this.filterChange({ logic: filter.logic, filters: newFilters });
  }

  private findFieldFilter(filter: CompositeFilterDescriptor, field: string): FilterDescriptor | null {
    for (const item of filter.filters) {
      if ('filters' in item) {
        const nested = this.findFieldFilter(item, field);
        if (nested) return nested;
      } else if (item.field === field) {
        return item;
      }
    }
    return null;
  }

  public addSource() {
    const modalRef = this.modalService.open(EditSourceComponent, { size: 'lg' });
    modalRef.componentInstance.isNew = true;

    modalRef.result.then((result) => {
      if (result?.action === 'save') {
        this.sourcesService.query(this.state, this.filter);
      }
    }, () => {});
  }

  public editSource(row: SourceResponseModel) {
    const modalRef = this.modalService.open(EditSourceComponent, { size: 'lg' });
    modalRef.componentInstance.isNew = false;
    modalRef.componentInstance.source = row;

    modalRef.result.then((result) => {
      if (result?.action === 'save') {
        this.sourcesService.query(this.state, this.filter);
      }
    }, () => {});
  }

  public deleteSource(row: SourceResponseModel) {
    const modalRef = this.modalService.open(ConfirmDeleteComponent);
    modalRef.componentInstance.title = 'Confirm Delete';
    modalRef.componentInstance.message = `Are you sure you want to delete source "${row.publishingSource}"?`;

    modalRef.result.then((result) => {
      if (result === 'confirmed') {
        this.sourcesService.delete(row.publishingSource).subscribe({
          next: () => {
            this.sourcesService.query(this.state, this.filter);
          },
          error: (error) => {
            console.error('Error deleting source:', error);
            alert('Failed to delete the source. Please try again.');
          }
        });
      }
    }, () => {});
  }
}
